import { useState, useRef } from 'react';
import { FieldPreview } from './FieldPreview';
import { Button } from '../ui/button';
import { GripVertical, Copy, Trash2, Plus, Settings as SettingsIcon } from 'lucide-react';

/**
 * The Jotform-style canvas — fields are rendered live as the candidate
 * would see them, with hover-revealed action toolbars and HTML5
 * drag-and-drop reordering. Clicking a field selects it for editing in
 * the right panel.
 *
 * Page breaks render as horizontal dividers labelled "Page N".
 */
export const InlineFormCanvas = ({
  fields,
  activeFieldId,
  onSelect,
  onDuplicate,
  onRemove,
  onReorder,
  onAddAt,
  onUpdate,
}) => {
  const [dragId, setDragId] = useState(null);
  const [dropTargetIdx, setDropTargetIdx] = useState(null);

  const handleDragStart = (e, fieldId) => {
    setDragId(fieldId);
    e.dataTransfer.effectAllowed = 'move';
    // Required by Firefox to start a drag
    try { e.dataTransfer.setData('text/plain', fieldId); } catch {}
  };

  const handleDragOver = (e, idx) => {
    if (!dragId) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDropTargetIdx(idx);
  };

  const handleDrop = (e, idx) => {
    e.preventDefault();
    if (!dragId) return;
    const fromIdx = fields.findIndex((f) => f.id === dragId);
    if (fromIdx < 0) {
      setDragId(null);
      setDropTargetIdx(null);
      return;
    }
    // When dropping at position `idx`, account for the removal of the
    // source item if it was earlier in the list.
    const adjustedTo = fromIdx < idx ? idx - 1 : idx;
    if (fromIdx !== adjustedTo) {
      onReorder(fromIdx, adjustedTo);
    }
    setDragId(null);
    setDropTargetIdx(null);
  };

  const handleDragEnd = () => {
    setDragId(null);
    setDropTargetIdx(null);
  };

  // Track current page number as we walk through fields so page-break
  // blocks render the correct "Page N → N+1" label.
  let pageCounter = 1;

  return (
    <div className="max-w-3xl mx-auto" data-testid="inline-form-canvas">
      {/* Drop zone at the very top */}
      <DropZone
        active={dropTargetIdx === 0 && dragId}
        onDragOver={(e) => handleDragOver(e, 0)}
        onDrop={(e) => handleDrop(e, 0)}
        onAdd={() => onAddAt(0)}
      />

      {fields.map((field, idx) => {
        if (field.field_type === 'page_break') {
          pageCounter += 1;
          return (
            <div key={field.id}>
              <PageBreakBlock
                field={field}
                fromPage={pageCounter - 1}
                toPage={pageCounter}
                active={activeFieldId === field.id}
                dragging={dragId === field.id}
                onSelect={() => onSelect(field.id)}
                onRemove={() => onRemove(field.id)}
                onDragStart={(e) => handleDragStart(e, field.id)}
                onDragEnd={handleDragEnd}
              />
              <DropZone
                active={dropTargetIdx === idx + 1 && dragId}
                onDragOver={(e) => handleDragOver(e, idx + 1)}
                onDrop={(e) => handleDrop(e, idx + 1)}
                onAdd={() => onAddAt(idx + 1)}
              />
            </div>
          );
        }
        return (
          <div key={field.id}>
            <CanvasField
              field={field}
              active={activeFieldId === field.id}
              dragging={dragId === field.id}
              onSelect={() => onSelect(field.id)}
              onDuplicate={() => onDuplicate(field.id)}
              onRemove={() => onRemove(field.id)}
              onLabelChange={(label) => onUpdate(field.id, { label })}
              onDragStart={(e) => handleDragStart(e, field.id)}
              onDragEnd={handleDragEnd}
            />
            <DropZone
              active={dropTargetIdx === idx + 1 && dragId}
              onDragOver={(e) => handleDragOver(e, idx + 1)}
              onDrop={(e) => handleDrop(e, idx + 1)}
              onAdd={() => onAddAt(idx + 1)}
            />
          </div>
        );
      })}
    </div>
  );
};

/**
 * A drop-zone gap that appears between fields. Doubles as a `+` insert
 * button when not dragging.
 */
function DropZone({ active, onDragOver, onDrop, onAdd }) {
  return (
    <div
      onDragOver={onDragOver}
      onDrop={onDrop}
      className={`relative transition-all ${active ? 'h-12' : 'h-4 group'}`}
    >
      {active && (
        <div className="absolute inset-x-0 top-1/2 h-0.5 bg-primary rounded-full" />
      )}
      {!active && (
        <button
          type="button"
          onClick={onAdd}
          className="absolute inset-x-0 top-1/2 -translate-y-1/2 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
          data-testid="canvas-insert-here-btn"
          aria-label="Insert field here"
        >
          <div className="flex items-center w-full">
            <div className="flex-1 h-px bg-primary/30" />
            <span className="mx-2 w-6 h-6 rounded-full bg-primary text-white flex items-center justify-center shadow-sm">
              <Plus className="w-3.5 h-3.5" />
            </span>
            <div className="flex-1 h-px bg-primary/30" />
          </div>
        </button>
      )}
    </div>
  );
}

/**
 * A single canvas field — renders the live preview with hover toolbars.
 */
function CanvasField({
  field, active, dragging, onSelect, onDuplicate, onRemove,
  onLabelChange, onDragStart, onDragEnd,
}) {
  const [editingLabel, setEditingLabel] = useState(false);
  const labelInputRef = useRef(null);

  return (
    <div
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onClick={onSelect}
      className={`group/field relative rounded-xl border bg-white p-4 transition-all cursor-pointer ${
        dragging ? 'opacity-40' : ''
      } ${
        active
          ? 'border-primary ring-2 ring-primary/20 shadow-sm'
          : 'border-transparent hover:border-primary/30 hover:shadow-sm'
      }`}
      data-testid={`canvas-field-${field.id}`}
    >
      {/* Drag handle (left edge) */}
      <div
        className="absolute -left-7 top-1/2 -translate-y-1/2 opacity-0 group-hover/field:opacity-100 transition-opacity cursor-grab active:cursor-grabbing p-1 text-muted-foreground"
        data-testid={`canvas-field-drag-${field.id}`}
      >
        <GripVertical className="w-4 h-4" />
      </div>

      {/* Hover action toolbar */}
      <div
        className={`absolute -top-3 right-2 flex items-center gap-1 transition-opacity ${
          active ? 'opacity-100' : 'opacity-0 group-hover/field:opacity-100'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-0.5 bg-white shadow-md rounded-full border border-border px-1 py-0.5">
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 rounded-full"
            onClick={(e) => { e.stopPropagation(); onSelect(); }}
            title="Edit"
            data-testid={`canvas-field-edit-${field.id}`}
          >
            <SettingsIcon className="w-3.5 h-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 rounded-full"
            onClick={(e) => { e.stopPropagation(); onDuplicate(); }}
            title="Duplicate"
            data-testid={`canvas-field-duplicate-${field.id}`}
          >
            <Copy className="w-3.5 h-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 rounded-full text-red-600 hover:bg-red-50"
            onClick={(e) => { e.stopPropagation(); onRemove(); }}
            title="Delete"
            data-testid={`canvas-field-remove-${field.id}`}
          >
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>

      {/* Inline editable label (click label to edit) */}
      {editingLabel ? (
        <input
          ref={labelInputRef}
          type="text"
          autoFocus
          value={field.label || ''}
          onChange={(e) => onLabelChange(e.target.value)}
          onBlur={() => setEditingLabel(false)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === 'Escape') {
              e.preventDefault();
              setEditingLabel(false);
            }
          }}
          onClick={(e) => e.stopPropagation()}
          className="text-sm font-medium border-b border-primary outline-none bg-transparent w-full mb-2"
          data-testid={`canvas-field-label-input-${field.id}`}
        />
      ) : (
        <div
          onDoubleClick={(e) => { e.stopPropagation(); setEditingLabel(true); }}
          className="cursor-text"
          data-testid={`canvas-field-label-${field.id}`}
        >
          <FieldPreview field={field} />
        </div>
      )}

      {/* Field-meta footer */}
      {(field.bullhorn_source_field || field.condition?.field_id) && (
        <div className="flex items-center gap-2 mt-3 pt-2 border-t border-dashed border-border text-[10px] uppercase tracking-wider text-muted-foreground">
          {field.bullhorn_source_field && (
            <span className="flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
              → {field.bullhorn_source_field}
            </span>
          )}
          {field.condition?.field_id && (
            <span className="flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-violet-500" />
              Conditional
            </span>
          )}
        </div>
      )}
    </div>
  );
}

function PageBreakBlock({
  field, fromPage, toPage, active, dragging, onSelect, onRemove, onDragStart, onDragEnd,
}) {
  return (
    <div
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onClick={onSelect}
      className={`group/page relative my-3 rounded-lg border-2 border-dashed bg-slate-50 px-4 py-3 cursor-pointer transition-all ${
        dragging ? 'opacity-40' : ''
      } ${
        active
          ? 'border-violet-400 bg-violet-50'
          : 'border-slate-300 hover:border-violet-300 hover:bg-violet-50/50'
      }`}
      data-testid={`canvas-page-break-${field.id}`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold uppercase tracking-wider text-violet-700">
            ── Page {fromPage} ends ─ Page {toPage} starts ──
          </span>
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6 text-red-600 hover:bg-red-50 opacity-0 group-hover/page:opacity-100"
          onClick={(e) => { e.stopPropagation(); onRemove(); }}
          data-testid={`canvas-page-break-remove-${field.id}`}
        >
          <Trash2 className="w-3 h-3" />
        </Button>
      </div>
    </div>
  );
}
